/**
 * This class implements a Least Recently Used (LRU) cache with a fixed maximum size.
 * When the cache is full, it removes the least recently used key-value pair.
 */
export class LRUCache<K, V> {
  private cache = new Map<K, V>();
  private maxSize: number;

  constructor(maxSize: number) {
    if (maxSize <= 0) {
      throw new Error('Cache size must be greater than 0');
    }
    this.maxSize = maxSize;
  }

  /**
   * Adds a key-value pair at the end of the cache.
   * If the cache is full, it removes the least recently used key-value pair.
   *
   * @param key
   * @param value
   */
  add(key: K, value: V): void {
    if (this.cache.has(key)) {
      this.cache.delete(key);
    }

    this.cache.set(key, value);

    if (this.cache.size > this.maxSize) {
      const oldestKey = this.cache.keys().next().value;
      this.cache.delete(oldestKey);
    }
  }

  /**
   * Gets the value associated with the key.
   * If the key exists, it moves the key-value pair to the end of the cache
   * to indicate that it was the most recently used.
   *
   * @param key
   * @returns
   */
  get(key: K): V | undefined {
    if (!this.cache.has(key)) {
      return undefined;
    }

    const value = this.cache.get(key);
    this.cache.delete(key);
    this.cache.set(key, value);

    return value;
  }

  forEach(callbackfn: (value: V, key: K, cache: this) => void): void {
    this.cache.forEach((value, key) => {
      callbackfn(value, key, this);
    });
  }

  has(key: K): boolean {
    return this.cache.has(key);
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/utils/lru-cache.ts